import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  ArrowLeftIcon,
  MapPinIcon,
  CalendarDaysIcon,
  CloudIcon,
  WrenchScrewdriverIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { theme } from '../../styles/theme';
import api from '../../services/api';

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background: ${theme.colors.background.primary};
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  padding: 0.5rem 1rem;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${theme.colors.primary.main};
    color: ${theme.colors.primary.main};
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const FormCard = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  box-shadow: ${theme.shadows.sm};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const Required = styled.span`
  color: ${theme.colors.error.main};
  margin-left: 0.25rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  background: ${theme.colors.background.tertiary};
  color: ${theme.colors.text.primary};
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background: ${theme.colors.background.card};
  }
  
  &:invalid {
    border-color: ${theme.colors.error.main};
  }
  
  &::placeholder {
    color: ${theme.colors.text.disabled};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  background: ${theme.colors.background.tertiary};
  color: ${theme.colors.text.primary};
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background: ${theme.colors.background.card};
  }
  
  &::placeholder {
    color: ${theme.colors.text.disabled};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: 1rem;
  background: ${theme.colors.background.tertiary};
  color: ${theme.colors.text.primary};
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background: ${theme.colors.background.card};
  }
  
  option {
    background: ${theme.colors.background.tertiary};
    color: ${theme.colors.text.primary};
  }
`;

const TripTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const TripTypeOption = styled.div`
  border: 2px solid ${props => props.selected ? theme.colors.primary.main : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? theme.colors.background.card : theme.colors.background.tertiary};
  
  &:hover {
    border-color: ${theme.colors.primary.main};
    background: ${theme.colors.background.card};
  }
`;

const TripTypeIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  color: ${props => props.selected ? theme.colors.primary.main : theme.colors.text.secondary};
`;

const TripTypeLabel = styled.div`
  font-weight: 500;
  color: ${theme.colors.text.primary};
`;

const TripTypeDescription = styled.div`
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
  margin-top: 0.25rem;
`;

const LocationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const CoordinateGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  padding: 0.75rem 2rem;
  border-radius: ${theme.borderRadius.md};
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  
  ${props => {
    if (props.variant === 'primary') {
      return `
        background: ${theme.colors.primary.main};
        color: ${theme.colors.primary.contrast};
        border: none;
        
        &:hover:not(:disabled) {
          background: ${theme.colors.primary.dark};
          transform: translateY(-1px);
        }
      `;
    } else {
      return `
        background: ${theme.colors.background.tertiary};
        color: ${theme.colors.text.primary};
        border: 1px solid ${theme.colors.border.primary};
        
        &:hover {
          border-color: ${theme.colors.primary.main};
          background: ${theme.colors.background.card};
        }
      `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error.main};
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  color: ${theme.colors.success.main};
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LoadingSpinner = styled.div`
  width: 1rem;
  height: 1rem;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const CreateTripForm = () => {
  const navigate = useNavigate();
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    trip_name: '',
    vessel_id: '',
    start_port: '',
    start_latitude: '',
    start_longitude: '',
    trip_description: '',
    trip_type: 'recreational',
    weather_conditions: '',
    sea_conditions: ''
  });

  const tripTypes = [
    {
      id: 'recreational',
      name: 'Recreational',
      description: 'Pleasure cruising, day trips',
      icon: MapPinIcon
    },
    {
      id: 'fishing',
      name: 'Fishing',
      description: 'Fishing expeditions',
      icon: MapPinIcon
    },
    {
      id: 'commercial',
      name: 'Commercial',
      description: 'Business or charter trips',
      icon: MapPinIcon
    },
    {
      id: 'maintenance',
      name: 'Maintenance',
      description: 'Engine testing, sea trials',
      icon: WrenchScrewdriverIcon
    }
  ];

  useEffect(() => {
    loadVessels();
  }, []);

  const loadVessels = async () => {
    try {
      const response = await api.get('/api/vessels');
      // Handle both array response and object with vessels property
      const vesselsData = Array.isArray(response.data) ? response.data : (response.data.vessels || []);
      setVessels(vesselsData);
    } catch (error) {
      console.error('Error loading vessels:', error);
      setError('Failed to load vessels');
      setVessels([]); // Ensure vessels is always an array
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTripTypeSelect = (tripType) => {
    setFormData(prev => ({
      ...prev,
      trip_type: tripType
    }));
  };

  const validateForm = () => {
    if (!formData.trip_name.trim()) {
      setError('Trip name is required');
      return false;
    }
    if (!formData.vessel_id) {
      setError('Please select a vessel');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.post('/api/trips', formData);
      
      setSuccess('Trip created successfully!');
      setTimeout(() => {
        navigate(`/trips/${response.data.trip.id}`);
      }, 1500);
      
    } catch (error) {
      console.error('Error creating trip:', error);
      setError(error.response?.data?.error || 'Failed to create trip');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormContainer>
      <Header>
        <BackButton onClick={() => navigate('/trips')}>
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Trips
        </BackButton>
        <Title>Create New Trip</Title>
      </Header>

      <FormCard>
        <form onSubmit={handleSubmit}>
          <FormGrid>
            {/* Left Column */}
            <div>
              <FormGroup>
                <Label>
                  Trip Name <Required>*</Required>
                </Label>
                <Input
                  type="text"
                  name="trip_name"
                  value={formData.trip_name}
                  onChange={handleInputChange}
                  placeholder="Enter trip name"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Vessel <Required>*</Required>
                </Label>
                <Select
                  name="vessel_id"
                  value={formData.vessel_id}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a vessel</option>
                  {vessels.map((vessel) => (
                    <option key={vessel.id} value={vessel.id}>
                      {vessel.vessel_name}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Trip Type</Label>
                <TripTypeGrid>
                  {tripTypes.map((tripType) => (
                    <TripTypeOption
                      key={tripType.id}
                      selected={formData.trip_type === tripType.id}
                      onClick={() => handleTripTypeSelect(tripType.id)}
                    >
                      <TripTypeIcon selected={formData.trip_type === tripType.id}>
                        <tripType.icon className="w-5 h-5" />
                      </TripTypeIcon>
                      <TripTypeLabel>{tripType.name}</TripTypeLabel>
                      <TripTypeDescription>{tripType.description}</TripTypeDescription>
                    </TripTypeOption>
                  ))}
                </TripTypeGrid>
              </FormGroup>

              <FormGroup>
                <Label>Description</Label>
                <TextArea
                  name="trip_description"
                  value={formData.trip_description}
                  onChange={handleInputChange}
                  placeholder="Describe your trip plans, destinations, or any special notes..."
                />
              </FormGroup>
            </div>

            {/* Right Column */}
            <div>
              <FormGroup>
                <Label>Starting Location</Label>
                <Input
                  type="text"
                  name="start_port"
                  value={formData.start_port}
                  onChange={handleInputChange}
                  placeholder="e.g., Granville Island, Vancouver"
                />
              </FormGroup>

              <FormGroup>
                <Label>Starting Coordinates (Optional)</Label>
                <CoordinateGroup>
                  <Input
                    type="number"
                    step="any"
                    name="start_latitude"
                    value={formData.start_latitude}
                    onChange={handleInputChange}
                    placeholder="Latitude"
                  />
                  <Input
                    type="number"
                    step="any"
                    name="start_longitude"
                    value={formData.start_longitude}
                    onChange={handleInputChange}
                    placeholder="Longitude"
                  />
                </CoordinateGroup>
              </FormGroup>

              <FormGroup>
                <Label>Weather Conditions</Label>
                <Input
                  type="text"
                  name="weather_conditions"
                  value={formData.weather_conditions}
                  onChange={handleInputChange}
                  placeholder="e.g., Clear skies, light winds"
                />
              </FormGroup>

              <FormGroup>
                <Label>Sea Conditions</Label>
                <Input
                  type="text"
                  name="sea_conditions"
                  value={formData.sea_conditions}
                  onChange={handleInputChange}
                  placeholder="e.g., Calm, slight chop"
                />
              </FormGroup>
            </div>
          </FormGrid>

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && (
            <SuccessMessage>
              <CheckIcon className="w-4 h-4" />
              {success}
            </SuccessMessage>
          )}

          <FormActions>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/trips')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <LoadingSpinner />
                  Creating...
                </>
              ) : (
                'Create Trip'
              )}
            </Button>
          </FormActions>
        </form>
      </FormCard>
    </FormContainer>
  );
};

export default CreateTripForm;
